import React from "react";
import PropTypes from "prop-types";
import DepartmentBadge from "./DepartmentBadge";
import { useDepartments } from "./DepartmentsContext";
import { useTenantRole } from "../../hooks/useTenantRole";
import {
  assignmentPayloadFromValue,
  currentAssignmentValue,
  memberDisplayName,
  resolveDepartment,
} from "./messageCenterHelpers";
import styles from "./AssigneeSelect.module.css";

/**
 * Seletor de atribuição da conversa (F2.4 · gate por papel na F2.7).
 *
 * **OWNER / ADMIN** — `<select>` nativo com dois grupos (acessível e navegável
 * por teclado por padrão, sem lib nova):
 *  - **Setores** → dispara round-robin (`PATCH { departmentId }`); o backend
 *    escolhe o atendente do setor.
 *  - **Atendentes** → atribuição direta (`PATCH { assignedToId }`).
 *  - **Desatribuir** (só quando já há atribuição) → `PATCH { assignedToId: null }`.
 *
 * O valor é controlado pela sessão (verdade do servidor): numa falha de
 * atribuição, o `<select>` volta sozinho ao estado anterior. O setor efetivo
 * aparece num chip abaixo, já que ao atribuir a uma pessoa o `<select>` passa a
 * mostrar o atendente e esconderia o setor.
 *
 * **MEMBER** — não pode delegar a setores/terceiros (o backend responde 403), e
 * a UI esconde o `<select>` inteiro, deixando apenas um resumo somente-leitura da
 * atribuição atual. As ações que o MEMBER PODE fazer (pegar/desatribuir a sua
 * própria conversa) vivem na barra de atribuição do `ConversationThread`.
 *
 * Enquanto o papel não é conhecido (carregando), tratamos como não-delegável — é
 * o default defensivo: nunca pisca opções proibidas para quem não pode usá-las.
 */
export default function AssigneeSelect({ session, onAssign, busy = false }) {
  const { activeDepartments, members, departmentsById, membersById, loading } =
    useDepartments();
  const { role } = useTenantRole();
  const canDelegate = role === "OWNER" || role === "ADMIN";

  const value = currentAssignmentValue(session);
  const department = resolveDepartment(session?.departmentId, departmentsById);
  const assignedMember = session?.assignedToId
    ? membersById[session.assignedToId]
    : null;
  const hasAssignment = Boolean(session?.assignedToId || session?.departmentId);

  const selectId = `assignee-select-${session?.id ?? "none"}`;

  const handleChange = (e) => {
    if (busy) return;
    const payload = assignmentPayloadFromValue(e.target.value);
    if (payload) onAssign(payload);
  };

  // MEMBER (ou papel ainda desconhecido): sem seletor de delegação — só o
  // resumo somente-leitura de quem está com a conversa.
  if (!canDelegate) {
    return (
      <div className={styles.wrap}>
        <span className={styles.label}>Atribuição</span>
        <div className={styles.controls}>
          {department ? (
            <DepartmentBadge
              name={department.name}
              color={department.color}
              compact
            />
          ) : (
            !assignedMember && (
              <span className={styles.assignee}>Não atribuída</span>
            )
          )}
        </div>
        {assignedMember && (
          <span className={styles.assignee}>
            Atendente: {memberDisplayName(assignedMember)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <label htmlFor={selectId} className={styles.label}>
        Atribuição
      </label>
      <div className={styles.controls}>
        <select
          id={selectId}
          className={styles.select}
          value={value}
          onChange={handleChange}
          disabled={busy || loading}
          aria-busy={busy}
        >
          <option value="">
            {loading ? "Carregando…" : "Não atribuída"}
          </option>

          {activeDepartments.length > 0 && (
            <optgroup label="Setores (distribui automaticamente)">
              {activeDepartments.map((d) => (
                <option key={d.id} value={`dept:${d.id}`}>
                  {d.name}
                </option>
              ))}
            </optgroup>
          )}

          {members.length > 0 && (
            <optgroup label="Atendentes">
              {members.map((m) => (
                <option key={m.id} value={`member:${m.id}`}>
                  {memberDisplayName(m)}
                </option>
              ))}
            </optgroup>
          )}

          {hasAssignment && (
            <optgroup label="Ações">
              <option value="unassign">Desatribuir</option>
            </optgroup>
          )}
        </select>

        {department && (
          <DepartmentBadge
            name={department.name}
            color={department.color}
            compact
          />
        )}
      </div>

      {assignedMember && (
        <span className={styles.assignee}>
          Atendente: {memberDisplayName(assignedMember)}
        </span>
      )}
    </div>
  );
}

AssigneeSelect.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string,
    assignedToId: PropTypes.string,
    departmentId: PropTypes.string,
  }),
  onAssign: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};
